import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import deepEqual from 'deep-equal';

import {
  AddressForm,
  apiCallFinished,
  errorMessages,
  errorMessagesActions,
  errorTypes,
  Messages,
  messagesActions,
  PostcodeSearchFormDescription as Description,
  scrollToMessageTypeAnchor,
  SearchFormContainer,
  infoTypes,
  EditAppointmentTitle,
  withRestorableDraft,
  withRequiredPrecondition,
  withProgressBar,
  warningModalSelectors,
  envConfigSelectors,
} from '~/components';

import {
  addressesApi,
  appointmentDraftActions,
  appointmentDraftHelpers,
  appointmentDraftSelectors,
  journeyStateActions,
  locationApi,
  shopsActions,
} from '~/api';

import { getRandomNumber, scrollToAnchor, isEmpty } from '~/utils';
import { validation } from '~/components/questions/helpers';
import { triggerEditPageChangesMadeAnalytics } from '~/analytics/analytics.listener';

import { EditButtonsWrapper } from '../../components/edit-buttons-wrapper';
import { formatToTemporaryState } from '../../helpers';
import {
  setEditMode,
  saveTemporaryAppointment,
  saveTemporaryJourneyState,
} from '../../edit-appointment.actions';
import {
  getTokenFromStorage,
  setTokenToStorage,
  TEMPORARY_TOKEN_NAME,
} from '../../../../../components/persist-token/persist-token.utils';

const { validateGBPostcode, POSTCODE_INVALID_ERROR_MESSAGE } = validation;

const defaultAddress = 'default';

const {
  NO_ADDRESSES_FOUND_ERROR,
  ADDRESS_NOT_ENTERED_ERROR,
  ADDRESS_NOT_SELECTED_ERROR,
  NO_POSTCODE_FOUND_ERROR,
  POSTCODE_OUT_OF_AREA_ERROR,
} = errorTypes;
const { ADDRESS_NOT_FOUND } = infoTypes;
const { formatAddressQuestionToDraftAddress, formatDraftAddressToAddressQuestion } =
  appointmentDraftHelpers;

export class EditAddress extends PureComponent {
  state = {
    context: {
      searchFormTitle: 'Find a shop',
      isSearchTermInvalid: false,
      searchFormInlineErrorMsg: POSTCODE_INVALID_ERROR_MESSAGE,
    },
    addresses: null,
    isAddressFormVisible: false,
    restoreFromForm: false,
    selectedAddress: defaultAddress,
    isAjaxCompleted: false,
    isCurrentLocationLoading: false,
  };

  componentDidMount() {
    const {
      dispatchApiCallFinished,
      dispatchSetEditMode,
      storeSearchQuery,
      address,
      draft,
      journeyState,
      temporaryAppointment,
      temporaryJourneyState,
      dispatchSaveTemporaryAppointment,
      dispatchSaveTemporaryJourneyState,
    } = this.props;

    dispatchSetEditMode(true);
    if (isEmpty(temporaryAppointment)) {
      dispatchSaveTemporaryAppointment(formatToTemporaryState(draft));
      setTokenToStorage(getTokenFromStorage(), TEMPORARY_TOKEN_NAME);
    }
    if (isEmpty(temporaryJourneyState)) {
      dispatchSaveTemporaryJourneyState(journeyState);
      setTokenToStorage(getTokenFromStorage(), TEMPORARY_TOKEN_NAME);
    }
    const selectedAddress = address ? JSON.stringify(address) : defaultAddress;

    if (storeSearchQuery) {
      dispatchApiCallFinished(false);
      addressesApi
        .fetchAddresses(storeSearchQuery)
        .then((addresses) => {
          const foundAddress = addresses.find((a) => deepEqual(a, address));

          const isManualAddress = !!address && !foundAddress;

          this.setState({
            addresses,
            isAjaxCompleted: true,
            isAddressFormVisible: isManualAddress || !addresses.length,
            restoreFromForm: isManualAddress,
            selectedAddress: foundAddress ? JSON.stringify(foundAddress) : defaultAddress,
          });
        })
        .then(() => dispatchApiCallFinished(true));
    } else {
      dispatchApiCallFinished(true);
      // Show address form if there's an address with details (manual entry)
      const hasManualAddress = address && Object.values(address).some((val) => val && val !== '');
      this.setState({
        isAjaxCompleted: true,
        isAddressFormVisible: hasManualAddress,
        selectedAddress,
      });
    }
    window.scrollTo(0, 0);
  }

  findHandler = (searchQuery) => {
    const { dispatchApiCallFinished, dispatchSetSearchQuery } = this.props;
    const errorMessage = validateGBPostcode(searchQuery);

    if (errorMessage) {
      return this.invalidPostcodeHandler(errorMessage, searchQuery);
    }
    dispatchApiCallFinished(false);
    dispatchSetSearchQuery(searchQuery);

    return this.executeIfServiceAvailable(searchQuery, () => this.findAddresses(searchQuery));
  };

  executeIfServiceAvailable = (postcode, execute) => {
    const { checkPostcodeAvailability } = this.props;
    return checkPostcodeAvailability
      ? checkPostcodeAvailability(postcode).then(
          () => execute(),
          ({ status }) => {
            if (status === 422) {
              this.showOutOfAreaError();
            }
          },
        )
      : execute();
  };

  showOutOfAreaError = () => {
    const { dispatchApiCallFinished, dispatchResetErrorMessagesWith } = this.props;

    const actionMessage = {
      type: POSTCODE_OUT_OF_AREA_ERROR,
    };
    dispatchResetErrorMessagesWith([actionMessage]);
    this.setState(
      {
        addresses: null,
      },
      () => dispatchApiCallFinished(true),
    );
  };

  findAddresses = (searchQuery) => {
    const { dispatchApiCallFinished, dispatchClearErrorMessages, dispatchPushMessage } = this.props;

    return addressesApi.fetchAddresses(searchQuery).then((addresses) => {
      dispatchClearErrorMessages();
      if (!addresses.length) {
        dispatchPushMessage({
          id: getRandomNumber(0, 1000),
          type: ADDRESS_NOT_FOUND,
        });
        this.setState(
          {
            restoreFromForm: false,
            isAddressFormVisible: true,
            addresses: null,
          },
          () => dispatchApiCallFinished(true),
        );
      } else {
        this.setState(
          (prevState) => ({
            restoreFromForm: false,
            selectedAddress: defaultAddress,
            isAddressFormVisible: false,
            context: {
              ...prevState.context,
              isSearchTermInvalid: false,
            },
            addresses,
          }),
          () => dispatchApiCallFinished(true),
        );
      }
    });
  };

  useCurrentLocationHandler = (coordinates) => {
    this.setState({
      isCurrentLocationLoading: true,
    });

    locationApi.getPostcode(coordinates).then(({ postcode }) => {
      this.setState({ isCurrentLocationLoading: false });
      this.findHandler(postcode);
    });
  };

  invalidPostcodeHandler = (errorMessage, searchQuery) => {
    const { dispatchSetSearchQuery } = this.props;
    dispatchSetSearchQuery(searchQuery);
    this.setState((prevState) => ({
      context: {
        ...prevState.context,
        isSearchTermInvalid: true,
        searchFormInlineErrorMsg: errorMessage,
      },
      addresses: null,
    }));
  };

  shopsNotFoundErrorHandler = (searchQuery) => {
    const { dispatchResetErrorMessagesWith } = this.props;
    const actionMessage = {
      type: NO_POSTCODE_FOUND_ERROR,
      description: errorMessages[NO_POSTCODE_FOUND_ERROR].createDescription(searchQuery),
    };
    dispatchResetErrorMessagesWith([actionMessage]);
    // Form validation is now handled by AddressForm's react-final-form validation
    return Promise.reject();
  };

  toggleAddressFormState = () => {
    const { isAddressFormVisible, context } = this.state;
    const {
      dispatchClearMessages,
      dispatchClearErrorMessages,
      dispatchSetSearchQuery,
      dispatchSelectAddress,
    } = this.props;

    dispatchClearMessages();
    dispatchClearErrorMessages();
    if (isAddressFormVisible) {
      scrollToAnchor('address-title');
      dispatchSetSearchQuery('');
      dispatchSelectAddress(null);
      this.setState({
        addresses: null,
        isAddressFormVisible: false,
        context: {
          ...context,
          isSearchTermInvalid: false,
        },
      });
    } else {
      this.setState({
        isAddressFormVisible: true,
      });
      triggerEditPageChangesMadeAnalytics('appointments', 'appointments:address');
    }
  };

  constructInitialValues = () => {
    const { address, storeSearchQuery } = this.props;
    const { restoreFromForm } = this.state;
    if (restoreFromForm) {
      return { address: formatDraftAddressToAddressQuestion(address) };
    }
    return { address: { postcode: storeSearchQuery } };
  };

  selectAddressHandler = (event) => {
    const { value: selectedAddress } = event.target;
    const { dispatchClearErrorMessages } = this.props;
    dispatchClearErrorMessages();
    this.setState({
      selectedAddress,
    });
    triggerEditPageChangesMadeAnalytics('appointments', 'appointments:address');
  };

  proceedToNextPage = () => {
    const { history, nextPath, dispatchClearErrorMessages, dispatchClearMessages } = this.props;

    dispatchClearMessages();
    dispatchClearErrorMessages();
    history.pushWithToken(nextPath);
  };

  onNextClick = () => {
    const { selectedAddress } = this.state;
    const {
      storeSearchQuery,

      dispatchResetErrorMessagesWith,
      dispatchSelectAddressDescription,
      dispatchUpdateAppointmentType,
      dispatchApiCallFinished,
      dispatchLoadShops,
      dispatchSetEmptyAddress,
      dispatchSelectAddress,

      isMeasureService,
    } = this.props;

    if (selectedAddress === defaultAddress) {
      dispatchResetErrorMessagesWith([{ type: ADDRESS_NOT_SELECTED_ERROR }]);
    } else {
      const addressDescription = JSON.parse(selectedAddress);

      dispatchApiCallFinished(false);

      let promiseResult;

      if (isMeasureService) {
        promiseResult = Promise.all([
          dispatchSelectAddressDescription(addressDescription),
          addressesApi
            .fetchAddress(addressDescription.id)
            .then((address) => dispatchSelectAddress(address)),
        ]);
      } else {
        promiseResult = Promise.all([
          dispatchSelectAddressDescription(addressDescription),
          dispatchSetEmptyAddress(),
          dispatchLoadShops(storeSearchQuery).then(({ shops }) => this.selectShop(shops[0])),
        ]);
      }

      promiseResult
        .then(() => dispatchUpdateAppointmentType())
        .then(() => this.proceedToNextPage());
    }
  };

  onNextAddressFormClick = ({ address }) => {
    const { dispatchApiCallFinished } = this.props;
    dispatchApiCallFinished(false);
    this.executeIfServiceAvailable(address.postcode, () => {
      this.addressFormSubmit(address);
    });
  };

  addressFormSubmit = (address) => {
    const {
      dispatchLoadShops,
      dispatchSelectAddress,
      dispatchSetEmptyAddressDescription,
      dispatchApiCallFinished,
      dispatchUpdateAppointmentType,
      isMeasureService,
    } = this.props;

    const loadShopPromise = isMeasureService
      ? Promise.resolve()
      : dispatchLoadShops(address.postcode).then(({ shops }) =>
          shops.length > 0
            ? this.selectShop(shops[0])
            : this.shopsNotFoundErrorHandler(address.postcode),
        );
    loadShopPromise
      .then(() => dispatchUpdateAppointmentType())
      .then(() => dispatchSelectAddress(formatAddressQuestionToDraftAddress({ ...address })))
      .then(() => dispatchSetEmptyAddressDescription())
      .then(this.proceedToNextPage)
      .catch(() => dispatchApiCallFinished(true));
  };

  selectShop = (shop) => {
    const { selectedShop, dispatchSelectShop } = this.props;

    if (selectedShop.id !== shop.id) {
      dispatchSelectShop(shop);
    }
  };

  render() {
    const {
      draft,
      journeyState,
      history,
      location,
      showWarningModal,
      storeSearchQuery,
      temporaryAppointment,
      temporaryJourneyState,
      dispatchSetDraftAppointment,
      dispatchSetEditMode,
      dispatchApiCallFinished,
      dispatchResetErrorMessagesWith,
      dispatchRemoveSingleErrorMessage,
      dispatchSaveTemporaryAppointment,
      postcodeSearchFormAdditionalInfo,
      dispatchSetJourneyState,
      dispatchSaveTemporaryJourneyState,
      dispatchClearMessages,
    } = this.props;
    const {
      context,
      isAjaxCompleted,
      addresses,
      isAddressFormVisible,
      selectedAddress,
      isCurrentLocationLoading,
    } = this.state;

    const postcodeFormContent = isAddressFormVisible ? (
      <AddressForm
        hideAddressFormHandler={this.toggleAddressFormState}
        onSubmit={this.onNextAddressFormClick}
        initialValues={this.constructInitialValues()}
        clearMessages={messagesActions.clearMessages}
        resetErrorMessages={errorMessagesActions.resetErrorMessagesWith}
        removeSingleErrorMessage={dispatchRemoveSingleErrorMessage}
      >
        <Messages forTypes={[ADDRESS_NOT_ENTERED_ERROR, NO_POSTCODE_FOUND_ERROR]} />
        <EditButtonsWrapper
          displayButtons
          temporaryDraftAppointment={temporaryAppointment}
          history={history}
          location={location}
          showWarningModal={showWarningModal}
          state={formatToTemporaryState(draft)}
          journeyState={journeyState}
          bookingId={draft.id}
          setEditMode={dispatchSetEditMode}
          saveButtonExtraProps={{
            type: 'submit',
          }}
          saveTemporaryState={dispatchSaveTemporaryAppointment}
          saveTemporaryJourneyState={dispatchSaveTemporaryJourneyState}
          temporaryJourneyState={temporaryJourneyState}
          restoreAppointmentDraft={dispatchSetDraftAppointment}
          restoreJourneyState={dispatchSetJourneyState}
          dispatchClearMessages={dispatchClearMessages}
          initialFormValues={this.constructInitialValues()}
        />
      </AddressForm>
    ) : (
      <SearchFormContainer
        context={context}
        addresses={addresses}
        selectedAddress={selectedAddress}
        apiCallFinished={dispatchApiCallFinished}
        dispatchResetErrorMessagesWith={dispatchResetErrorMessagesWith}
        selectAddressHandler={this.selectAddressHandler}
        toggleAddressFormState={this.toggleAddressFormState}
        findHandler={this.findHandler}
        useCurrentLocationHandler={this.useCurrentLocationHandler}
        isCurrentLocationLoading={isCurrentLocationLoading}
        storeSearchQuery={storeSearchQuery}
        validate={validateGBPostcode}
      >
        <Messages forTypes={[NO_ADDRESSES_FOUND_ERROR]} />
        <Messages forTypes={[ADDRESS_NOT_SELECTED_ERROR]} />
        <EditButtonsWrapper
          displayButtons
          temporaryDraftAppointment={temporaryAppointment}
          history={history}
          location={location}
          showWarningModal={showWarningModal}
          state={formatToTemporaryState(draft)}
          journeyState={journeyState}
          bookingId={draft.id}
          saveHandler={this.onNextClick}
          setEditMode={dispatchSetEditMode}
          restoreAppointmentDraft={dispatchSetDraftAppointment}
          saveTemporaryAppointment={dispatchSaveTemporaryAppointment}
          hideSaveButton={!addresses}
          temporaryJourneyState={temporaryJourneyState}
          restoreJourneyState={dispatchSetJourneyState}
          dispatchClearMessages={dispatchClearMessages}
        />
      </SearchFormContainer>
    );
    const PostcodeSearchFormDescriptionScrollAnchor = scrollToMessageTypeAnchor(Description);

    return (
      <section data-testid="Postcode" data-test-ajax={isAjaxCompleted}>
        <EditAppointmentTitle />
        <PostcodeSearchFormDescriptionScrollAnchor
          additionalInfo={postcodeSearchFormAdditionalInfo}
          forTypes={[
            NO_ADDRESSES_FOUND_ERROR,
            ADDRESS_NOT_ENTERED_ERROR,
            ADDRESS_NOT_FOUND,
            ADDRESS_NOT_SELECTED_ERROR,
            NO_POSTCODE_FOUND_ERROR,
            POSTCODE_OUT_OF_AREA_ERROR,
          ]}
        />
        <Messages forTypes={[ADDRESS_NOT_FOUND, POSTCODE_OUT_OF_AREA_ERROR]} />
        {postcodeFormContent}
      </section>
    );
  }
}

export const mapStateToProps = (state) => {
  const {
    appointment: { draft },
    journeyState,
    editAppointment: { temporaryAppointment, temporaryJourneyState },
    services: {
      config: { PostcodeSearchForm = {}, editShopNextPath: nextPath },
    },
    router: { location },
  } = state;
  const warningModalState = warningModalSelectors.selectState(state);
  return {
    draft,
    journeyState,
    selectedShop: draft.shop,
    storeSearchQuery: journeyState.searchQuery,
    address: appointmentDraftSelectors.selectAddress(state),
    temporaryAppointment,
    temporaryJourneyState,
    nextPath,
    location,
    showWarningModal: !!warningModalState.isOpen,
    postcodeSearchFormAdditionalInfo: PostcodeSearchForm.additionalFormDescription,
    checkPostcodeAvailability: PostcodeSearchForm.checkPostcodeAvailability,
    isMeasureService: envConfigSelectors.selectIsMeasureService()(state),
  };
};

const mapDispatchToProps = {
  dispatchSetSearchQuery: journeyStateActions.setSearchQuery,
  dispatchUpdateAppointmentType: appointmentDraftActions.updateAppointmentType,
  dispatchLoadShops: shopsActions.loadShops,
  dispatchSelectShop: appointmentDraftActions.setShop,
  dispatchSelectAddress: appointmentDraftActions.setAddress,
  dispatchSetEmptyAddress: appointmentDraftActions.setEmptyAddress,
  dispatchSelectAddressDescription: appointmentDraftActions.setAddressDescription,
  dispatchSetEmptyAddressDescription: appointmentDraftActions.setEmptyAddressDescription,
  dispatchApiCallFinished: apiCallFinished,
  dispatchResetErrorMessagesWith: errorMessagesActions.resetErrorMessagesWith,
  dispatchClearErrorMessages: errorMessagesActions.clearErrorMessages,
  dispatchClearMessages: messagesActions.clearMessages,
  dispatchRemoveSingleErrorMessage: errorMessagesActions.removeSingleErrorMessage,
  dispatchPushMessage: messagesActions.pushMessage,
  dispatchSaveTemporaryAppointment: saveTemporaryAppointment,
  dispatchSetDraftAppointment: appointmentDraftActions.setDraftAppointment,
  dispatchSetEditMode: setEditMode,
  dispatchSetJourneyState: journeyStateActions.setJourneyState,
  dispatchSaveTemporaryJourneyState: saveTemporaryJourneyState,
};

export const ConnectedEditAddress = withRestorableDraft(
  withRequiredPrecondition(
    connect(mapStateToProps, mapDispatchToProps)(withProgressBar(EditAddress)),
  ),
);

export const ConnectedEditAddressForManageAppointment = connect(
  mapStateToProps,
  mapDispatchToProps,
)(EditAddress);

EditAddress.propTypes = {
  history: PropTypes.shape({}).isRequired,
  location: PropTypes.shape({}).isRequired,
  address: PropTypes.shape({}),
  nextPath: PropTypes.string,
  storeSearchQuery: PropTypes.string,
  selectedShop: PropTypes.shape({}).isRequired,
  draft: PropTypes.shape({
    shop: PropTypes.shape({}).isRequired,
  }).isRequired,
  temporaryAppointment: PropTypes.shape({}).isRequired,
  checkPostcodeAvailability: PropTypes.func,
  temporaryJourneyState: PropTypes.shape({}).isRequired,
  journeyState: PropTypes.shape({}).isRequired,
  showWarningModal: PropTypes.bool.isRequired,
  isMeasureService: PropTypes.bool.isRequired,

  dispatchApiCallFinished: PropTypes.func.isRequired,
  dispatchSetSearchQuery: PropTypes.func.isRequired,
  dispatchLoadShops: PropTypes.func.isRequired,
  dispatchSelectShop: PropTypes.func.isRequired,
  dispatchSelectAddress: PropTypes.func.isRequired,
  dispatchSetEmptyAddress: PropTypes.func.isRequired,
  dispatchSelectAddressDescription: PropTypes.func.isRequired,
  dispatchSetEmptyAddressDescription: PropTypes.func.isRequired,
  dispatchResetErrorMessagesWith: PropTypes.func.isRequired,
  dispatchUpdateAppointmentType: PropTypes.func.isRequired,
  dispatchClearErrorMessages: PropTypes.func.isRequired,
  dispatchPushMessage: PropTypes.func.isRequired,
  dispatchClearMessages: PropTypes.func.isRequired,
  dispatchRemoveSingleErrorMessage: PropTypes.func.isRequired,
  dispatchSaveTemporaryAppointment: PropTypes.func.isRequired,
  dispatchSetDraftAppointment: PropTypes.func.isRequired,
  dispatchSetEditMode: PropTypes.func.isRequired,
  postcodeSearchFormAdditionalInfo: PropTypes.node,
  dispatchSaveTemporaryJourneyState: PropTypes.func.isRequired,
  dispatchSetJourneyState: PropTypes.func.isRequired,
};

EditAddress.defaultProps = {
  address: null,
  storeSearchQuery: '',
  nextPath: 'edit-partner',
  postcodeSearchFormAdditionalInfo: undefined,
  checkPostcodeAvailability: undefined,
};
